import React, { useState, useEffect, useRef } from 'react';
import '../../AddChampion.css';
import './ChampionSelector.css';
import DataService from '../../../../../DataService';

interface Champion {
  id: string;
  name: string;
  imageUrl: string;
}

export interface ChampionReplaceData {
  name: string;
  level?: number;
  items?: string[];
  runes?: any;
  skillOrder?: string[];
}

interface ChampionSelectorProps {
  onSelectChampion: (championData: ChampionReplaceData) => void;
  onCancel: () => void;
  currentChampion: {
    id: string;
    name: string;
  };
}

const ChampionSelector: React.FC<ChampionSelectorProps> = ({ 
  onSelectChampion, 
  onCancel,
  currentChampion 
}) => {
  const [championsList, setChampionsList] = useState<Champion[]>([]);
  const [searchInput, setSearchInput] = useState('');
  const [suggestions, setSuggestions] = useState<Champion[]>([]);
  const [showSelector, setShowSelector] = useState(false);

  const inputRef = useRef<HTMLInputElement>(null);
  const selectorRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (inputRef.current && showSelector) {
      inputRef.current.focus();
    }
  }, [showSelector]);

  useEffect(() => {
    const fetchChampions = async () => {
      // Fetch latest version dynamically
      const version = await DataService.fetchLatestVersion();
      
      const response = await fetch(
        `https://ddragon.leagueoflegends.com/cdn/${version}/data/en_US/champion.json`
      );
      const data = await response.json();

      // Create our standard champion array
      const championsData = Object.values(data.data).map((champion: any) => ({
        id: champion.id,
        name: champion.name,
        imageUrl: `https://ddragon.leagueoflegends.com/cdn/${version}/img/champion/${champion.id}.png`,
      }));

      // Append the "Practice Dummy" champion to the list
      championsData.push({
        id: 'DummyChampion',
        name: 'DummyChampion',
        imageUrl:
          'https://raw.communitydragon.org/latest/plugins/rcp-be-lol-game-data/global/default/assets/characters/tft_trainingdummy/hud/icons2d/tft_trainingdummy_donothing.png',
      });

      setChampionsList(championsData);
    };
    fetchChampions();
  }, []);

  // Handle clicks outside the selector
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (selectorRef.current && !selectorRef.current.contains(event.target as Node)) {
        setShowSelector(false);
        setSearchInput('');
        setSuggestions([]);
      }
    };

    if (showSelector) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showSelector]);

  /** Normalize for fuzzy search: strip apostrophes, periods, spaces */
  const normalizeForSearch = (str: string): string => {
    return str.toLowerCase().replace(/['\.\s-]/g, '');
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target.value;
    setSearchInput(input);

    if (input.length > 0) {
      const normalizedInput = normalizeForSearch(input);
      const filteredChampions = championsList
        .filter((champion) => {
          const normalizedName = normalizeForSearch(champion.name);
          const normalizedId = normalizeForSearch(champion.id);
          return normalizedName.includes(normalizedInput) || normalizedId.includes(normalizedInput);
        })
        .sort((a, b) => {
          const normA = normalizeForSearch(a.name);
          const normB = normalizeForSearch(b.name);
          const aStartsWith = normA.startsWith(normalizedInput);
          const bStartsWith = normB.startsWith(normalizedInput);
          if (aStartsWith && !bStartsWith) return -1;
          if (!aStartsWith && bStartsWith) return 1;
          return a.name.localeCompare(b.name);
        })
        .slice(0, 8);
      setSuggestions(filteredChampions);
    } else {
      setSuggestions([]);
    }
  };

  const handleChampionSelect = (champion: Champion) => {
    const championData: ChampionReplaceData = {
      name: champion.name,
      level: 11, // Default level
    };
    onSelectChampion(championData);

    // Reset form
    setSearchInput('');
    setSuggestions([]);
    setShowSelector(false);
  };

  const toggleSelector = () => {
    setShowSelector(!showSelector);
    if (!showSelector) {
      setSearchInput('');
      setSuggestions([]);
    }
  };

  return (
    <div className="champion-selector">
      <div className="current-champion">
        <h4>Current Champion: {currentChampion.name}</h4>
        <button 
          className="change-champion-btn" 
          onClick={toggleSelector}
        >
          Change Champion
        </button>
      </div>
      
      {showSelector && (
        <div className="champion-search-dropdown" ref={selectorRef}>
          <div className="champion-search-container">
            <input
              ref={inputRef}
              type="text"
              placeholder="Search for a champion"
              value={searchInput}
              onChange={handleInputChange}
              className="champion-search-input"
            />
            
            {suggestions.length > 0 && (
              <ul className="champion-suggestions-list">
                {suggestions.map((champion) => (
                  <li
                    key={champion.id}
                    className="champion-suggestion-item"
                    onClick={() => handleChampionSelect(champion)}
                  >
                    <img src={champion.imageUrl} alt={champion.name} />
                    <span>{champion.name}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ChampionSelector; 